import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import AsyncTable, { Column, RequestPage } from '../../../../components/AsyncTable';
import NewRegisterBtn from '../../../../components/Buttons/NewRegisterBtn';
import { backendErrorNotification } from '../../../../components/Notification';
import { ActionButtonsContainer, DeleteIconTable, EditIconTable } from '../../../../components/TablePaginator/styles';
import { orderApprovalGroupsApi } from '../../../../services/requests';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import { OrderApprovalGroup } from '../../../../types/apiResponse/orderApprovalGroup';
import { ApiResPaginated } from '../../../../types/apiResponseTypes';
import checkPermission from '../../../../utils/checkPermission';
import ApprovalGroupFormModal from './Components/ApprovalGroupFormModal';
import DeleteApprovalGroupModal from './Components/DeleteApprovalGroupModal';

const ApprovalGroupsPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [data, setData] = useState<OrderApprovalGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<OrderApprovalGroup | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [updateTable, setUpdateTable] = useState(0);

  const closeModals = () => {
    setShowForm(false);
    setShowDelete(false);
    setSelectedGroup(null);
  };
  const refresh = () => setUpdateTable((value) => value + 1);

  const columns: Column<OrderApprovalGroup>[] = [
    { label: t('id') as string, accessor: 'id' },
    { label: t('description') as string, accessor: 'description' },
    { label: t('audience') as string, Cell: (row) => row.audience?.description ?? t('notFound') as string },
    { label: t('approvalAuthority') as string, accessor: 'authority' },
    {
      label: t('actions') as string,
      Cell: (row) => (
        <ActionButtonsContainer>
          {checkPermission('orderApprovalGroup-edit') && (
            <EditIconTable title={t('edit') as string} onClick={() => {
              setSelectedGroup(row);
              setShowForm(true);
            }} />
          )}
          {checkPermission('orderApprovalGroup-delete') && (
            <DeleteIconTable title={t('delete') as string} onClick={() => {
              setSelectedGroup(row);
              setShowDelete(true);
            }} />
          )}
        </ActionButtonsContainer>
      ),
    },
  ];

  const requestPage: RequestPage = async ({ search, page, quantityPerPage }) => {
    try {
      orderApprovalGroupsApi.query = `?search=${encodeURIComponent(search)}&page=${page}&quantityPerPage=${quantityPerPage}`;
      const response = await orderApprovalGroupsApi.index();
      const result: ApiResPaginated<OrderApprovalGroup[]> = response.data;
      return {
        data: result.data,
        hasMore: page < result.paginated.totalPage,
        totalPage: result.paginated.totalPage,
      };
    } catch (err) {
      if (!orderApprovalGroupsApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
      return { data: [], hasMore: false, totalPage: 0 };
    }
  };

  return (
    <ContentContainer title={t('approvalGroups') as string}>
      {checkPermission('orderApprovalGroup-create') && (
        <NewRegisterBtn onClick={() => setShowForm(true)} style={{ marginBottom: '1rem', marginLeft: 0 }} />
      )}
      <AsyncTable
        tableName={t('approvalGroups') as string}
        columns={columns}
        value={data}
        onChange={setData}
        requestPage={requestPage}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: theme.colors.surface,
            secondaryColor: theme.colors.onSurface,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage') as string,
          searchPlaceholder: t('search') as string,
        }}
      />
      {showForm && (
        <ApprovalGroupFormModal selectedGroup={selectedGroup} closeModal={closeModals} callback={refresh} />
      )}
      {showDelete && selectedGroup && (
        <DeleteApprovalGroupModal selectedGroup={selectedGroup} closeModal={closeModals} callback={refresh} />
      )}
    </ContentContainer>
  );
};

export default ApprovalGroupsPage;
